import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';
import { CartPaymentCard } from '@ridango/components/lib/purchase/purchase.model';

@Component({
    selector: 'gl-ridango-saved-payment-method',
    templateUrl: './saved-payment-method.component.html',
    styleUrls: ['./saved-payment-method.component.scss']
})
export class SavedPaymentMethodComponent {
    @Input() card: CartPaymentCard;
    @Input() isSelected: boolean;

    @Output() methodSelected: EventEmitter<any> = new EventEmitter();

    constructor(
        @Inject('ENV') public env: Env,
        private translate: TranslateService
    ) {}

    /**
     * Builds an aria-label for a saved payment method.
     * The label is optimized for screen readers and includes:
     * - card type (or a fallback label),
     * - last four digits read digit-by-digit,
     * - optional expiry date,
     * - and current selection state.
     */
    buildAriaLabel(card: CartPaymentCard, isSelected: boolean): string {
        const cardType = card?.type
            ? this.translate.instant(card.type)
            : this.translate.instant('SAVED_PAYMENT_METHOD_CARD_FALLBACK');

        const endingIn = this.translate.instant('SAVED_PAYMENT_METHOD_ENDING_IN');
        const expiresLabel = this.translate.instant('SAVED_PAYMENT_METHOD_EXPIRES');
        const selectedState = this.translate.instant(
            isSelected ? 'SAVED_PAYMENT_METHOD_SELECTED' : 'SAVED_PAYMENT_METHOD_NOT_SELECTED'
        );

        const lastFourDigits = card?.lastFourDigits ?? '';

        // Split digits with spaces so screen readers read them one by one
        // (e.g. "0004" → "0 0 0 4"), including leading zeros.
        const lastFourDigitsSpaced = lastFourDigits ? String(lastFourDigits).padStart(4, '0').split('').join(' ') : '';

        const month = card?.expirationMonth ? String(card.expirationMonth).padStart(2, '0') : null;
        const year = card?.expirationYear ? String(card.expirationYear) : null;
        const expiry = month && year ? `, ${expiresLabel} ${month}/${year}` : '';

        return `${cardType} ${endingIn} ${lastFourDigitsSpaced}${expiry}, ${selectedState}`;
    }

    omMethodSelected() {
        this.methodSelected.emit(this.card);
    }
}
